/**
 * Shared literal-parsing primitives for the inverse-parse branches
 * (`predicateJsonBranch.ts` for MSSQL/MySQL/Oracle/SQLite,
 * `predicateJsonBranchPg.ts` for PostgreSQL, `predicateInListBranch.ts` for the
 * value lists (`IN (...)` / `NOT IN (...)`), and the scalar branch in
 * `predicateToFilterCondition.ts`). Kept in its own leaf module (rather than
 * exported from either branch file) so neither branch module needs to import a
 * VALUE from the other — `predicateJsonBranch.ts` imports
 * `parsePgJsonPredicate` FROM `predicateJsonBranchPg.ts`; if the Pg module
 * imported these helpers back from the non-Pg module that would be a runtime
 * import cycle.
 *
 * These invert exactly the escaping `common/utils/jsonFilterSql.ts` and
 * `common/utils/filterWhereBuilder.ts` (the emitters) apply — see
 * `escapeAsTextLiteral` / `escapeAsTextInList` and `escapeValue` for the
 * forward direction, `core/utils/mysqlSqlLiteral.ts` for the MySQL text-literal
 * rules, `core/utils/sqlServerTextLiteral.ts` for SQL Server's `N'…'`, and
 * `containsLikeSql.ts` for the contains patterns. Do not modify the emitters;
 * these are read-only mirrors of their literal shapes.
 */

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import { SQL_SERVER_SUPPLEMENTARY_COLLATION } from '../../../../core/utils/sqlServerTextLiteral';
import type { FilterCondition, JsonSubOperator } from '../../core/types/workspaceTypes';

/** Escape regex metacharacters so a literal string can be embedded in a
 *  larger regex source. */
export function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Reverse of the emitter's SQL string-literal quoting (doubled `'` → `'`). */
export function unescapeSqlString(raw: string): string {
  return raw.replace(/''/g, "'");
}

/**
 * The MySQL hexadecimal text literal `quoteMysqlText` emits (see
 * `src/core/utils/mysqlSqlLiteral.ts`): `_utf8mb4 X'<hex>'`, used for every
 * value containing `\` or NUL because a quoted MySQL literal changes meaning
 * with `NO_BACKSLASH_ESCAPES`.
 *
 * Only the `_utf8mb4` introducer is recognised — that is exactly what the
 * emitter produces, and the whole inverse-parse family is a read-only mirror
 * of the emitter. A bare `X'41'` / `0x41` is deliberately NOT decoded: on a
 * numeric column `flags = 0x41` is a user-typed NUMBER, and rewriting it as
 * the text `'A'` on chip re-emit would silently change the predicate. Other
 * introducers (`_binary`, `_latin1`) name non-UTF-8 encodings whose bytes we
 * cannot decode faithfully. Anything not matched falls through to today's
 * raw-text fallback — never a best-guess chip.
 */
const MYSQL_HEX_LITERAL_SRC = `_utf8mb4\\s*[xX]'[0-9A-Fa-f]*'`;

/** Decode `_utf8mb4 X'<hex>'` back to the original string. Returns `null` when
 *  the text is not that shape, has an odd nibble count, or does not decode as
 *  UTF-8 (all "not our emit" — the caller bails to raw text). */
export function decodeMysqlHexLiteral(raw: string): string | null {
  const m = raw.trim().match(/^_utf8mb4\s*[xX]'([0-9A-Fa-f]*)'$/);
  if (!m) return null;
  const hex = m[1];
  if (hex.length % 2 !== 0) return null;
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  try {
    // `ignoreBOM`: a value starting with U+FEFF is a legitimate (if odd) filter
    // value — the default decoder would swallow it and re-emitting the chip
    // would silently drop a character from the comparison.
    return new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(bytes);
  } catch {
    return null;
  }
}

/** SQL Server's `COLLATE` after a value outside the BMP (`sqlServerTextLiteral.ts`), as a regex source. */
export const MSSQL_COLLATE_SUFFIX_SRC = `\\s+COLLATE\\s+${SQL_SERVER_SUPPLEMENTARY_COLLATION}`;
const MSSQL_COLLATE_SUFFIX_RE = new RegExp(`${MSSQL_COLLATE_SUFFIX_SRC}$`, 'i');

/**
 * Decode a text literal in a value position back to its string. Handles the
 * quoted form on every engine, on SQL Server also the `N'…'` form and the
 * `COLLATE` a value outside the BMP takes (`sqlServerTextLiteral.ts`) and, on
 * MySQL only, the `_utf8mb4 X'<hex>'` form. Returns `null` for anything
 * else — including text that only starts
 * and ends with a quote (`'a' || 'b'` is an expression, not one literal) and,
 * on MySQL, a quoted literal with a backslash escape: what `'C:\new'` means
 * depends on the session's `NO_BACKSLASH_ESCAPES`, and the emitter writes such
 * a value in the hex form, so reading it back would change the filter. (`\%`
 * and `\_` keep their backslash in both modes, so a LIKE pattern still reads.)
 */
export function decodeTextLiteral(raw: string, engine?: DatabaseEngineId): string | null {
  const t = engine === 'mssql' ? raw.trim().replace(MSSQL_COLLATE_SUFFIX_RE, '').replace(/^N(?=')/, '') : raw.trim();
  if (/^'(?:[^']|'')*'$/.test(t)) {
    return engine === 'mysql' && /\\(?![%_])/.test(t) ? null : unescapeSqlString(t.slice(1, -1));
  }
  return engine === 'mysql' ? decodeMysqlHexLiteral(t) : null;
}

/**
 * Regex source for a text literal in a value position, capturing the WHOLE
 * literal in ONE group (feed the capture to `decodeTextLiteral`). On MySQL the
 * alternation also admits the `_utf8mb4 X'<hex>'` form the emitter produces
 * for backslash-/NUL-bearing values, on SQL Server the `N'…'` form and its
 * `COLLATE`; the other engines keep the plain quoted shape.
 */
export function textValueSrc(engine?: DatabaseEngineId): string {
  if (engine === 'mysql') return `((?:${MYSQL_HEX_LITERAL_SRC})|'(?:[^']|'')*')`;
  return engine === 'mssql' ? `(N?'(?:[^']|'')*'(?:${MSSQL_COLLATE_SUFFIX_SRC})?)` : `('(?:[^']|'')*')`;
}

/** Regex source for a SQL single-quoted string literal, capturing the inner
 *  (still-doubled-quote-escaped) text. Engine-agnostic; prefer
 *  `textValueSrc` + `decodeTextLiteral` in value positions so the MySQL hex
 *  form is admitted too. */
export const STRING_VALUE_SRC = `'((?:[^']|'')*)'`;

/** Regex source for a bare numeric literal (int or decimal, optional sign) —
 *  the shape `escapeValueFn` emits for a JS `number` value. */
export const NUMERIC_VALUE_SRC = `(-?\\d+(?:\\.\\d+)?)`;

/**
 * Regex source for a quoted JSONPath literal (`quotePathLiteral`'s output),
 * capturing the WHOLE literal in ONE group (feed it to `decodeTextLiteral`).
 * JSONPath text never legitimately contains a `'`, so no doubled-quote
 * handling — a path with a literal quote simply won't match (conservative:
 * falls through to `null`). On MySQL `quotePathLiteral` routes through
 * `quoteMysqlText`, so a path containing a backslash (a JSON key such as
 * `$."a\b"`) emits as `_utf8mb4 X'<hex>'` and is admitted here too; on SQL
 * Server a path outside ASCII is written `N'…'` (`sqlServerTextLiteral.ts`).
 */
export function pathLiteralSrc(engine?: DatabaseEngineId): string {
  if (engine === 'mysql') return `((?:${MYSQL_HEX_LITERAL_SRC})|'[^']*')`;
  return engine === 'mssql' ? `(N?'[^']*')` : `('[^']*')`;
}

/**
 * Split a SQL list body (the inside of `IN (...)`) on TOP-LEVEL commas —
 * i.e. commas outside of a single-quoted string literal and outside nested
 * parentheses (an element may be a call: Oracle's `TO_DATE('…', '…')`).
 * Returns `null` for an unterminated quote or unbalanced parentheses
 * (malformed input — conservative bail).
 */
export function splitTopLevelCommaList(s: string): string[] | null {
  const parts: string[] = [];
  let cur = '';
  let inQuote = false;
  let depth = 0;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === "'") {
      if (inQuote && s[i + 1] === "'") {
        cur += "''";
        i += 1;
        continue;
      }
      inQuote = !inQuote;
      cur += ch;
      continue;
    }
    if (!inQuote && ch === '(') depth += 1;
    if (!inQuote && ch === ')') {
      depth -= 1;
      if (depth < 0) return null;
    }
    if (ch === ',' && !inQuote && depth === 0) {
      parts.push(cur);
      cur = '';
      continue;
    }
    cur += ch;
  }
  if (inQuote || depth !== 0) return null;
  parts.push(cur);
  return parts;
}

/**
 * Parse an `IN (...)` list body into a `string[]`, requiring EVERY element
 * to be a text literal (mirrors `escapeAsTextLiteral` / `escapeAsTextInList`,
 * which always quote enum/JSON-IN values as text — see their header comments).
 * On MySQL an element may also be the `_utf8mb4 X'<hex>'` form the emitter
 * produces for a backslash-/NUL-bearing value, so a mixed list such as
 * `IN ('plain', _utf8mb4 X'433a5c6e6577')` round-trips. Any other element
 * shape (bare numeric, `NULL`, a function call) fails the whole list —
 * conservative: the emitted SQL our `enum` / `property_enum` operators produce
 * is ALWAYS all-text, so a non-text element means this ISN'T our own emit and
 * must not be guessed at.
 */
export function parseQuotedStringList(inner: string, engine?: DatabaseEngineId): string[] | null {
  const parts = splitTopLevelCommaList(inner);
  if (!parts || parts.length === 0) return null;
  const out: string[] = [];
  for (const raw of parts) {
    const decoded = decodeTextLiteral(raw, engine);
    if (decoded === null) return null;
    out.push(decoded);
  }
  return out;
}

/** A recognized JSON predicate: the field and its structured `json` condition. */
export type JsonParseResult = { field: string; condition: FilterCondition };

/** A matched path-bearing JSON predicate shape (every sub-op but is_valid). */
export type JsonPathMatch = {
  field: string;
  path: string;
  declaredOnText: boolean;
  value?: FilterCondition['value'];
};

/** The numeric JSON sub-operators and the SQL comparison each one emits. */
export const JSON_NUMERIC_SUB_OPS: ReadonlyArray<[JsonSubOperator, '>' | '>=' | '<' | '<=']> = [
  ['path_gt', '>'],
  ['path_gte', '>='],
  ['path_lt', '<'],
  ['path_lte', '<='],
];

/**
 * Try `tries` in order and turn the first hit into `{ field, condition }`.
 * Shared by the PG and non-PG parsers so the condition shape is built once;
 * a shape that carries no value (has_property, path_is_null) gets no `value`
 * key, exactly as before.
 */
export function firstJsonPathMatch(
  tries: ReadonlyArray<readonly [JsonSubOperator, () => JsonPathMatch | null]>,
): JsonParseResult | null {
  for (const [sub, match] of tries) {
    const m = match();
    if (!m) continue;
    return {
      field: m.field,
      condition: {
        operator: 'json',
        jsonSubOperator: sub,
        path: m.path,
        ...(m.value !== undefined ? { value: m.value } : {}),
        jsonDeclaredOnText: m.declaredOnText,
      },
    };
  }
  return null;
}
